import React from "react";
import { Link } from "react-router-dom";
import { Container, SectionLabel, TrustChip } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { HeartHandshake, BadgeCheck, FileText, CheckCircle2 } from "lucide-react";

const NP_IMG = "https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg";

export default function Nonprofits() {
  return (
    <div>
      <section className="border-b border-ff-border bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-2 lg:items-center lg:py-20">
          <div>
            <SectionLabel>For Nonprofits</SectionLabel>
            <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight text-ff-blue sm:text-5xl">
              Verified nonprofits, trusted receipts
            </h1>
            <p className="mt-5 max-w-xl text-ff-dark">
              U.S. nonprofits can get verified to earn the Nonprofit Verified badge and unlock basic receipt
              support for donors. Advanced nonprofit tools are coming soon.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TrustChip label="Nonprofit Verified" size="md" />
              <TrustChip label="Campaign Reviewed" size="md" />
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                <Link to="/nonprofits/verify" data-testid="np-get-verified">Get Verified</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-ff-blue font-semibold text-ff-blue hover:bg-ff-blue hover:text-white">
                <Link to="/start-a-fundraiser" data-testid="np-start">Start a Nonprofit Campaign</Link>
              </Button>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-ff-border shadow-md">
            <img src={NP_IMG} alt="Volunteers supporting the community" className="h-full max-h-[80vh] w-full object-cover" />
          </div>
        </Container>
      </section>

      <section className="bg-[#F3F4F6]">
        <Container className="py-16">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: BadgeCheck, t: "Earn the badge", d: "Verification is required before the Nonprofit Verified badge appears on your campaigns." },
              { icon: FileText, t: "Donor receipts", d: "Basic receipt support in v1 for verified nonprofits, with tax-appropriate language where legally allowed." },
              { icon: HeartHandshake, t: "Built for U.S. 501(c)(3)s", d: "U.S. nonprofits only. Submit your EIN and organization details to begin verification." },
            ].map((f) => (
              <div key={f.t} className="rounded-xl border border-ff-border bg-white p-8 shadow-sm">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-ff-green/10">
                  <f.icon className="h-6 w-6 text-ff-green" />
                </span>
                <h3 className="font-heading text-lg font-bold text-ff-blue">{f.t}</h3>
                <p className="mt-2 text-sm text-ff-dark">{f.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-white">
        <Container className="py-16">
          <div className="rounded-2xl border border-ff-border bg-ff-blue p-10 text-white">
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">What you'll need to verify</h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {["Organization legal name", "EIN (Employer Identification Number)", "Organization address", "Contact email", "Supporting documents (if requested)", "Truthfulness attestation"]
                .map((x) => (
                  <li key={x} className="flex items-center gap-2.5 text-white/85">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-white/60" /> {x}
                  </li>
                ))}
            </ul>
          </div>
        </Container>
      </section>
    </div>
  );
}
